/**
 * RP-32.4 — a Plan Line's notes, in a side drawer over the plan grid. Internal only:
 * readable and writable by everyone who can see the plan, never exported or shared.
 */

import { X } from "lucide-react";
import { useEffect } from "react";
import { CommentThread } from "@/components/plans/CommentThread";
import type { PlanLineView } from "@/schemas/resource-plans";

export function PlanLineNotesDrawer({
  planId,
  line,
  onClose,
}: {
  planId: string;
  line: PlanLineView;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="Close notes"
        className="fixed inset-0 z-40 bg-foreground/20"
        onClick={onClose}
      />
      <aside
        aria-label="Line notes"
        className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-[480px] flex-col border-l border-border bg-background shadow-xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-bold">Notes</h2>
            <p className="truncate text-sm text-muted-foreground">
              {line.laborCategoryName}
              {line.clientLaborCategory !== null && ` · ${line.clientLaborCategory}`}
              {line.suggestedResourceName !== null && ` · ${line.suggestedResourceName}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <CommentThread
            planId={planId}
            scope={{ planLineId: line.id }}
            emptyText="No notes on this line yet. Considerations and dependencies live here, with who wrote them and when."
          />
        </div>
      </aside>
    </>
  );
}
